'use client';

import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';

interface PromotionModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

export function PromotionModal({
  open,
  onClose,
  title,
  icon,
  children,
  className,
}: PromotionModalProps) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onClose}>
      <DialogPrimitive.Portal>
        {/* Overlay */}
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-[9500] bg-black/50 backdrop-blur-sm',
            'data-[state=open]:animate-[dialog-overlay-show_200ms_ease-out]',
            'data-[state=closed]:animate-[dialog-overlay-hide_150ms_ease-in]',
          )}
        />

        {/* Content */}
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className={cn(
            'fixed z-[9500] flex flex-col',
            'bg-[var(--bg-elevated)]',
            'overflow-hidden outline-none',
            // Mobile: fullscreen slide-up
            'inset-0 rounded-none',
            'data-[state=open]:animate-[promo-slide-up_350ms_cubic-bezier(0.32,0.72,0,1)]',
            'data-[state=closed]:animate-[promo-slide-down_250ms_cubic-bezier(0.32,0.72,0,1)]',
            // Desktop: centered card with scale
            'md:inset-auto md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2',
            // Модалка — объём ОСТАВЛЕН (ответ 35, второй вопрос = 2).
            'md:w-full md:max-w-2xl md:h-auto md:max-h-[90vh] md:rounded-card md:shadow-deep',
            'md:border md:border-[var(--border-default)]',
            'md:data-[state=open]:animate-[dialog-content-show_250ms_cubic-bezier(0.16,1,0.3,1)]',
            'md:data-[state=closed]:animate-[dialog-content-hide_150ms_ease-in]',
            className,
          )}
        >
          {/* Header */}
          <div
            className="flex-shrink-0 border-b border-[var(--border-default)] bg-gradient-to-r from-[var(--primary-subtle)] to-[var(--primary-muted)]"
            style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
          >
            <div className="flex items-center gap-3 px-4 py-3 md:px-6 md:py-4">
              {/* Icon */}
              <div className="w-10 h-10 md:w-11 md:h-11 rounded-card bg-[var(--primary)] flex items-center justify-center flex-shrink-0">
                {icon}
              </div>

              {/* Title */}
              <DialogPrimitive.Title className="flex-1 min-w-0 text-lg md:text-xl font-bold font-heading text-[var(--text-primary)] truncate">
                {title}
              </DialogPrimitive.Title>

              {/* Close */}
              <button
                onClick={onClose}
                className="w-9 h-9 md:w-10 md:h-10 rounded-pill flex items-center justify-center hover:bg-[var(--surface-hover)] active:bg-[var(--surface-active)] transition-colors text-[var(--text-secondary)] hover:text-[var(--text-primary)] flex-shrink-0"
              >
                <X className="w-5 h-5" weight="bold" />
              </button>
            </div>
          </div>

          {/* Content — scrollable */}
          <div
            className="flex-1 overflow-y-auto overscroll-contain"
            style={{
              WebkitOverflowScrolling: 'touch',
              touchAction: 'pan-y',
              minHeight: 0,
              paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            }}
          >
            {children}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
